import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useContext } from 'react';

import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import FilesQueryContext from '~pages/Approvals/Files/FilesQueryContext';
import useBillingLineGroupsQuery from '~queries/useBillingLineGroupsQuery';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { sideLabelKeys } from '../chargeSelection';
import { NO_VALUE_PLACEHOLDER } from '../chargesGrid';
import { ChargesGroup, sumLineGroupsBySide } from '../chargesGrouping';
import ChargesGroupCheckbox from './ChargesGroupCheckbox';

const EMPHASIZED_TEXT_SX = { fontSize: '14px', fontWeight: 700, lineHeight: '20px' };

const captionSx = (theme: Theme) => ({
  color: theme.brandV2.colors.treadGray2,
  fontSize: '10px',
  lineHeight: '14px',
});

interface ChargesGroupHeaderProps {
  group: ChargesGroup;
  isOpen: boolean;
  onToggle: () => void;
  totalsJobIds: string[];
}

export default function ChargesGroupHeader({
  group,
  isOpen,
  onToggle,
  totalsJobIds,
}: ChargesGroupHeaderProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { refreshCount } = useContext(FilesQueryContext);
  const { isError, isUpdating, lineGroups } = useBillingLineGroupsQuery(
    companyId,
    totalsJobIds,
    refreshCount,
  );
  const sideTotals = sumLineGroupsBySide(lineGroups ?? []);

  return (
    <Box
      sx={(theme) => ({
        alignItems: 'center',
        bgcolor: theme.semantic.surfaceRaised,
        borderBottom: isOpen ? `solid 1px ${theme.brandV2.colors.treadGray7}` : 'none',
        display: 'flex',
        pl: 2,
      })}
    >
      <ChargesGroupCheckbox group={group} />
      <Box
        aria-expanded={isOpen}
        component="button"
        onClick={onToggle}
        sx={{
          alignItems: 'center',
          bgcolor: 'transparent',
          border: 'none',
          color: 'inherit',
          cursor: 'pointer',
          display: 'flex',
          flex: 1,
          font: 'inherit',
          gap: 2,
          minWidth: 0,
          px: 2,
          py: 1.25,
          textAlign: 'left',
        }}
      >
        {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={EMPHASIZED_TEXT_SX}>{group.name}</Typography>
          <Typography
            sx={(theme) => ({
              color: theme.brandV2.colors.treadGray2,
              fontSize: '12px',
              lineHeight: '16px',
            })}
          >
            {[
              group.subtitle,
              t('approvals.approve_charges.groups.jobs', { count: group.jobIds.length }),
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        </Box>

        {sideTotals.map((sideTotal) => (
          <Box key={sideTotal.side} sx={{ minWidth: 140, textAlign: 'right' }}>
            <Typography sx={captionSx}>{t(sideLabelKeys[sideTotal.side])}</Typography>
            <GroupSideTotal
              isError={isError}
              isRead={lineGroups !== null}
              isUpdating={isUpdating}
              sideTotal={sideTotal}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

interface GroupSideTotalProps {
  isError: boolean;
  isRead: boolean;
  isUpdating: boolean;
  sideTotal: ReturnType<typeof sumLineGroupsBySide>[number];
}

function GroupSideTotal({
  isError,
  isRead,
  isUpdating,
  sideTotal: { eligibleCount, lineCount, side, total },
}: GroupSideTotalProps) {
  const { currencyFormatter } = useCompanyCurrency();

  if (!isRead && !isError) {
    return <Skeleton sx={{ ml: 'auto', width: 80 }} variant="text" />;
  }

  const hasNoTotal = isError || lineCount === 0;

  if (hasNoTotal) {
    return <Typography sx={EMPHASIZED_TEXT_SX}>{NO_VALUE_PLACEHOLDER}</Typography>;
  }

  return (
    <Box
      aria-busy={isUpdating}
      data-test-id={`group-${side}-totals`}
      sx={{ opacity: isUpdating ? 0.5 : 1 }}
    >
      <Typography sx={EMPHASIZED_TEXT_SX}>{currencyFormatter(total)}</Typography>
      <Typography sx={captionSx}>
        {[
          t('approvals.approve_charges.line_count', { count: lineCount }),
          t('approvals.approve_charges.groups.ready', { count: eligibleCount }),
        ].join(' · ')}
      </Typography>
    </Box>
  );
}
